/* -------------------------------------------------------------------------
 * Sayuri animation core
 * -------------------------------------------------------------------------
 * Shared infrastructure for per-scent signature animations. This file is
 * loaded once on every PDP that has a scent metaobject linked. Individual
 * scent animation files (sayuri-maple-fall.js, sayuri-sakura-petals.js,
 * sayuri-citrus-mist.js, ...) self-register with this core via
 * `window.SayuriAnimations.register(key, { mount, css })`.
 *
 * Contract for a scent animation module:
 *   SayuriAnimations.register('maple-fall', {
 *     mount: function(element, ctx) {
 *       // element is the <div data-sayuri-signature="maple-fall">
 *       // ctx exposes: seed(number) → deterministic PRNG,
 *       //              accent, accentSoft (from --scent-* CSS vars),
 *       //              intensity ('whisper' | 'soft' | 'lush')
 *       // Append particle DOM into `element`.
 *     },
 *     css: '...keyframes string...'   // injected once on first register
 *   });
 *
 * All signature animations must:
 *   - Respect prefers-reduced-motion (core handles the guard; skipped entirely)
 *   - Be deterministic via the seeded PRNG (layouts mustn't jitter on re-mount)
 *   - Use colors tuned FOR THEIR SCENT (particle palettes are intentionally
 *     hardcoded per scent even though scent UI accent is merchant-controlled;
 *     e.g. a "noir" scent might still want white snow particles).
 * ----------------------------------------------------------------------- */
(function () {
  'use strict';

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var SIGNATURES = {};

  // Deterministic PRNG (mulberry32). Seed from the signature key so a given
  // scent always lays out particles the same way across sessions/rerenders.
  function seed(s) {
    return function () {
      s |= 0; s = (s + 0x6D2B79F5) | 0;
      var t = Math.imul(s ^ (s >>> 15), 1 | s);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  // Read per-scent CSS custom properties from body so scent modules
  // don't each re-query them.
  function readScentVars() {
    var cs = window.getComputedStyle(document.body);
    return {
      accent: (cs.getPropertyValue('--scent-accent') || '').trim() || '#A43A72',
      accentSoft: (cs.getPropertyValue('--scent-accent-soft') || '').trim() || '#FFC4D1',
      ink: (cs.getPropertyValue('--scent-ink') || '').trim() || '#2A1520'
    };
  }

  // Inject scent-specific CSS once per signature key.
  function injectCss(key, css) {
    var id = 'sayuri-anim-css-' + key;
    if (document.getElementById(id)) return;
    var style = document.createElement('style');
    style.id = id;
    style.textContent = css;
    document.head.appendChild(style);
  }

  function register(key, config) {
    SIGNATURES[key] = config;
    if (config.css) injectCss(key, config.css);
    // If the target element is already in the DOM waiting for this signature,
    // mount it now. Handles the case where the scent module loads AFTER the
    // initial tryMount() pass.
    tryMount();
  }

  function mount(el) {
    if (el.dataset.sayuriMounted) return;
    var key = el.getAttribute('data-sayuri-signature') || '';
    var sig = SIGNATURES[key];
    if (!sig) return; // module for this scent hasn't registered yet

    if (REDUCED) {
      el.classList.add('sayuri-signature--reduced');
      el.dataset.sayuriMounted = '1';
      return;
    }

    var vars = readScentVars();
    var intensity = el.getAttribute('data-sayuri-intensity') || 'soft';

    sig.mount(el, {
      seed: seed,
      accent: vars.accent,
      accentSoft: vars.accentSoft,
      ink: vars.ink,
      intensity: intensity
    });
    el.dataset.sayuriMounted = '1';
  }

  function tryMount() {
    var els = document.querySelectorAll('[data-sayuri-signature]:not([data-sayuri-mounted])');
    for (var i = 0; i < els.length; i++) mount(els[i]);
  }

  // Expose API. Scent modules use `register`; outside callers can trigger
  // `tryMount` (e.g. after ajax replace).
  window.SayuriAnimations = {
    register: register,
    mount: mount,
    tryMount: tryMount,
    seed: seed,
    signatures: SIGNATURES,
    reduced: REDUCED
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tryMount);
  } else {
    tryMount();
  }
})();
